import { authorize, authRefresh, switchUserProvider } from '../api/authApi'
import { tokenRepository } from '@/domains/auth/infrastructure/repositories/TokenRepository'
import { Token } from '@/domains/auth/domain/models/Token'
import { TokenWithPermissions } from '@/domains/auth/domain/models/TokenWithPermissions'
import { TokenFactory } from '@/domains/auth/application/services/TokenFactory'
import { User } from '@/domains/auth/domain/models/User'
import { userService } from './userService'

interface ApiError {
  error: string
}

class AuthService {
  async authenticateWithCredentials(
    email: string,
    password: string,
  ): Promise<void> {
    try {
      const authResponse = await authorize({ email, password })

      if (!authResponse.response.ok) {
        const apiError = authResponse.error as unknown as ApiError
        throw new Error(apiError?.error || 'Authentication failed')
      }

      if (!authResponse.data) {
        throw new Error('No authentication data received')
      }

      const { userData, accessToken, refreshToken } = authResponse.data

      const user = User.fromApiResponse(userData)
      await userService.saveUser(user)

      // Access token is always JWT in our system
      const accessTokenRecord = TokenFactory.createAccessToken(accessToken)
      // Refresh token is always a simple string
      const refreshTokenRecord = TokenFactory.createRefreshToken(
        refreshToken,
        1 * 24 * 3600,
      ) // 1 day

      tokenRepository.saveUserTokens(
        userData.id,
        accessTokenRecord,
        refreshTokenRecord,
      )
    } catch (error) {
      console.error('Failed to authenticate with credentials', error)
      throw error
    }
  }

  async switchProvider(providerId: string): Promise<void> {
    const user = await userService.getCurrentUser()
    const userId = user?.id

    if (!userId) {
      throw new Error('User not found!')
    }

    const result = await switchUserProvider(providerId)

    if (!result.response.ok || !result.data) {
      const apiError = result.error as unknown as ApiError
      throw new Error(apiError?.error || 'Failed to switch provider')
    }

    const { accessToken, refreshToken } = result.data

    // Access token is always JWT in our system
    const accessTokenRecord = TokenFactory.createAccessToken(accessToken)
    // Refresh token is always a simple string
    const refreshTokenRecord = TokenFactory.createRefreshToken(
      refreshToken,
      1 * 24 * 3600,
    ) // 1 day

    tokenRepository.saveUserTokens(
      userId,
      accessTokenRecord,
      refreshTokenRecord,
    )
  }

  async logout(): Promise<void> {
    const user = await userService.getCurrentUser()
    const userId = user?.id
    if (!userId) return

    tokenRepository.revokeAllUserTokens(userId)
    await userService.clearUser()
  }

  async getTokens(options?: { forceRefresh?: boolean }): Promise<{
    accessToken: TokenWithPermissions
    refreshToken: Token
  }> {
    try {
      const user = await userService.getCurrentUser()
      const userId = user?.id

      if (!userId) {
        throw new Error('User not found!')
      }

      const { accessTokenData, refreshTokenData } =
        await tokenRepository.findTokensByUserId(userId)

      // Create refresh token object to check expiration
      const refreshToken = refreshTokenData
        ? (TokenFactory.createFromStoredData(refreshTokenData) as Token)
        : null

      if (!refreshToken || refreshToken.isExpired())
        throw new Error('Session is expired!')

      // Create access token object to check expiration
      const accessToken = accessTokenData
        ? (TokenFactory.createFromStoredData(
            accessTokenData,
          ) as TokenWithPermissions)
        : null

      // Force refresh if requested, or if token is missing/expired
      const shouldRefresh =
        options?.forceRefresh || !accessToken || accessToken.isExpired()

      if (shouldRefresh) {
        const result = await authRefresh(refreshToken.value)

        if (!result.response.ok || !result.data?.access_token) throw result

        // Access token is always JWT
        const accessTokenRecord = TokenFactory.createAccessToken(
          result.data.access_token,
        )

        tokenRepository.saveUserTokens(userId, accessTokenRecord, refreshToken)

        return { accessToken: accessTokenRecord, refreshToken }
      }

      return { accessToken, refreshToken }
    } catch (error) {
      console.error('Failed to validate session', error)
      throw error
    }
  }

  async validateSession(): Promise<boolean> {
    try {
      const hasValidUser = await userService.hasValidUser()
      if (!hasValidUser) {
        return false
      }

      const user = await userService.getCurrentUser()

      const userId = user?.id

      if (!userId) {
        return false
      }

      const { accessTokenData, refreshTokenData } =
        await tokenRepository.findTokensByUserId(userId)

      const refreshToken = refreshTokenData
        ? (TokenFactory.createFromStoredData(refreshTokenData) as Token)
        : null

      const accessToken = accessTokenData
        ? (TokenFactory.createFromStoredData(
            accessTokenData,
          ) as TokenWithPermissions)
        : null

      if (!refreshToken || refreshToken.isExpired()) return false

      return !!accessToken && !accessToken.isExpired()
    } catch (error) {
      console.error('Failed to validate session', error)
      return false
    }
  }
}

export const authService = new AuthService()
